import {SettingLevel} from 'src/services/analytics/workspaceSettingsEvents';

import {MediaBrowserPanelConfig} from '../../../PanelMediaBrowser/common';
import {useWorkspaceMediaSettingsDrawer} from '../media/MediaSettingsDrawerContextProvider';
import {useSectionSettingsContextMaybe} from '../SectionSettingsContextProvider';
import {useWorkspaceInheritedSettingsForPanel} from './useWorkspaceInheritedSettingsForPanel';

export type VideoPlaybackSyncSettings = {
  isEnabled: boolean;
  isInherited: boolean;
  autoPlay: {
    isEnabled: boolean;
    isInherited: boolean;
  };
  autoLoop: {
    isEnabled: boolean;
    isInherited: boolean;
  };
};

const getInheritedSetting = (
  settingName: keyof MediaBrowserPanelConfig,
  settingsLevel: SettingLevel,
  config: MediaBrowserPanelConfig,
  workspaceSettings?: MediaBrowserPanelConfig,
  inheritedSettings?: MediaBrowserPanelConfig,
  sectionConfig?: MediaBrowserPanelConfig
) => {
  const isEnabled = inheritedSettings?.[settingName] || config[settingName];
  const workspaceValue = workspaceSettings?.[settingName];
  const sectionValue = sectionConfig?.[settingName];

  const isEnabledAtWorkspace = workspaceValue === true;
  const isEnabledAtSection = sectionValue === true;

  let isInherited = false;
  if (settingsLevel === 'section') {
    isInherited = isEnabledAtWorkspace;
  } else if (settingsLevel === 'panel') {
    isInherited = isEnabledAtWorkspace || isEnabledAtSection;
  }

  return {
    isEnabled: !!isEnabled,
    isInherited: isInherited,
  };
};

export const getVideoPlaybackSyncSetting = (
  config: MediaBrowserPanelConfig,
  settingsLevel: SettingLevel,
  workspaceSettings?: MediaBrowserPanelConfig,
  inheritedSettings?: MediaBrowserPanelConfig,
  sectionConfig?: MediaBrowserPanelConfig
): VideoPlaybackSyncSettings => {
  return {
    ...getInheritedSetting(
      'videoPlaybackSync',
      settingsLevel,
      config,
      workspaceSettings,
      inheritedSettings,
      sectionConfig
    ),
    autoPlay: getInheritedSetting(
      'videoPlaybackSyncAutoplay',
      settingsLevel,
      config,
      workspaceSettings,
      inheritedSettings,
      sectionConfig
    ),
    autoLoop: getInheritedSetting(
      'videoPlaybackSyncLoop',
      settingsLevel,
      config,
      workspaceSettings,
      inheritedSettings,
      sectionConfig
    ),
  };
};

export const useVideoPlaybackSyncSettings = (
  config: MediaBrowserPanelConfig,
  settingsLevel: SettingLevel
): VideoPlaybackSyncSettings => {
  const workspaceSettings = useWorkspaceMediaSettingsDrawer();
  const inheritedSettings = useWorkspaceInheritedSettingsForPanel();
  const sectionConfig = useSectionSettingsContextMaybe();
  return getVideoPlaybackSyncSetting(
    config,
    settingsLevel,
    workspaceSettings.config,
    inheritedSettings?.media,
    sectionConfig?.sectionMediaSettings
  );
};
